import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useLocation, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Building2, MailCheck, CircleAlert, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function VerifyEmailPage() {
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [state, setState] = useState(token ? "loading" : "missing");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await axios.post(`${API}/api/auth/verify-email`, { token }, { withCredentials: true });
        if (!cancelled) setState("success");
      } catch (err) {
        if (cancelled) return;
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "Verification failed");
        setState("error");
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6" data-testid="verify-page">
      <Card className="w-full max-w-md bg-[#0a0a0b] border-white/10 text-white p-8 text-center">
        <div className="flex items-center gap-3 mb-6 justify-center">
          <img src="/prestigious-logo.png" alt="Prestigious" className="h-12 w-12" />
        </div>

        {state === "loading" && (
          <div data-testid="verify-loading">
            <Loader2 className="h-10 w-10 animate-spin text-indigo-300 mx-auto mb-4" />
            <div className="text-lg font-semibold font-display">Verifying your email…</div>
          </div>
        )}
        {state === "success" && (
          <div data-testid="verify-success">
            <MailCheck className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold font-display mb-2">You&apos;re verified</h2>
            <p className="text-sm text-zinc-400 mb-6">
              Thanks! Your email is confirmed and we can now deliver statements, ARC decisions, and alerts to your inbox.
            </p>
            <Link to="/login" className="text-orange-300 hover:text-orange-200 underline">Sign in</Link>
          </div>
        )}
        {(state === "error" || state === "missing") && (
          <div data-testid={state === "missing" ? "verify-missing" : "verify-error"}>
            <CircleAlert className="h-12 w-12 text-amber-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold font-display mb-2">
              {state === "missing" ? "Missing verification token" : "Couldn't verify"}
            </h2>
            <p className="text-sm text-zinc-400 mb-6">
              {state === "missing"
                ? "This page was opened without a token. Please use the exact link from your welcome email."
                : (error || "This link may have expired or already been used.")}
            </p>
            <Link to="/login" className="text-orange-300 hover:text-orange-200 underline">Back to sign in</Link>
          </div>
        )}
      </Card>
    </div>
  );
}
